/**
 * Plan-tier feature gates.
 *
 * Maps each gateable feature (currently: slash-skill kinds) to the
 * minimum plan tier required to use it. Used by the task pane to show
 * an upsell banner when a Starter user picks a Growth+ feature.
 *
 * Soft gating only: the banner is informational, not enforced server-
 * side. A determined customer could still hit the underlying endpoint
 * with a crafted request. This is intentional for V1 — we want to
 * communicate the tier model without blocking real work while we learn
 * what customers actually use.
 *
 * Default tier map (founder decision 2026-05-25). Adjust as the product
 * roadmap evolves and we ship more tier-differentiating features.
 */
import type { PlanTier } from './authProfile';
import type { Skill, SkillKind } from './skills';

/** Numeric weight of each tier for ordering. */
export const TIER_RANK: Record<PlanTier, number> = {
  tier1: 1,
  tier2: 2,
  tier3: 3,
  tier4: 4,
};

/**
 * Minimum tier required for each skill kind. Skills NOT listed here are
 * available on every tier (the basic value-prop: ingest, chat, build
 * dashboards). Add to this map to gate a new feature.
 */
export const SKILL_KIND_MIN_TIER: Partial<Record<SkillKind, PlanTier>> = {
  // Growth+: advanced analytical / generation features that consume more
  // LLM tokens or hit more sophisticated builders.
  commentary: 'tier2',
  forecast: 'tier2',
  clean_data: 'tier2',
  // Wizard, scaffold (basic dashboards), and prompt-based skills remain
  // on Starter — those are the core value prop, available to everyone.
};

/**
 * Returns the minimum tier required for a skill, or null if the skill is
 * available on every tier.
 */
export function requiredTierForSkill(skill: Skill): PlanTier | null {
  return SKILL_KIND_MIN_TIER[skill.kind] ?? null;
}

/**
 * True when the user's plan meets (or exceeds) the required tier. A null
 * user plan (un-provisioned) never meets any tier requirement — but
 * un-provisioned users see PlansWelcome, not the chat UI, so this case
 * is theoretical.
 */
export function meetsTier(userPlan: PlanTier | null, requiredTier: PlanTier): boolean {
  if (!userPlan) return false;
  return TIER_RANK[userPlan] >= TIER_RANK[requiredTier];
}

/** UI label for a tier (e.g. "tier2" → "Growth"). */
export const TIER_LABEL: Record<PlanTier, string> = {
  tier1: 'Starter',
  tier2: 'Growth',
  tier3: 'Practice',
  tier4: 'Enterprise',
};
